import axios, { AxiosResponse } from "axios";
import DocumentSimplifiedDTO from "features/invoices/dtos/DocumentSimplifiedDTO";
import IFileService from "./IFileService";
import fileDownload from 'js-file-download';
import { Document_DocumentDownload } from "./endpoints";
import { injectable } from "inversify";
import { trackPromise } from "react-promise-tracker";
import IContentDialogService from "features/shared/components/contentDialog/IContentDialogService";
import { inversifyContainer } from "infrastructure/ioc/ioc.container";
import { IocTypes } from "infrastructure/ioc/ioc.types";
import React from "react";
import DataQuery from "./DataQuery";
import IDataHelper from "./IDataHelper";
import { getDynamicConfig } from "infrastructure/configuration/configurationHelper";
import { Nullable } from "features/shared/types/nullable";
import DocumentDTO from "features/shared/dtos/DocumentDTO";

const dynamicConfig = getDynamicConfig();

@injectable()
export default class FileService implements IFileService {
  private baseUrl: string;
  private readonly _dialogService: IContentDialogService;
  private readonly _dataHelper: IDataHelper;

  constructor() {
    this.baseUrl = dynamicConfig.flexiApiUrl || "";
    this._dialogService = inversifyContainer.get<IContentDialogService>(IocTypes.ContentDialogService);
    this._dataHelper = inversifyContainer.get<IDataHelper>(IocTypes.DataHelper);
  }
  
  async downloadDocument(
    documentInfo: DocumentSimplifiedDTO,
    loadingAreaName?: string,
    catchError: boolean = true
  ): Promise<void> {
    if (documentInfo?.documentId && documentInfo?.name) {
      let finalUrl =
        this.baseUrl +
        "/" +
        Document_DocumentDownload.replace(
          "{documentId}",
          documentInfo.documentId
        );

      try {
        let result = await trackPromise(
          axios.get(finalUrl, { responseType: "blob" }),
          loadingAreaName
        );
        fileDownload(result.data, documentInfo.name);
      } catch (error) {
        if (catchError) {
          // TODO: globální zpracování errorů
          this.handleError();
        }

        throw error;
      }
    }
  }

  async downloadFile(query: DataQuery, fileName?: string): Promise<void> {
    try {
      let response = await trackPromise(axios.get(
        this._dataHelper.getUrl(query, this.baseUrl),
        {
          cancelToken: query.cancelToken,
          params: query.params,
          responseType: "blob",
        }
      ));

      let name = this.getFileName(response, fileName);

      fileDownload(response.data, name);
    } catch (error) {
      // TODO: globální zpracování errorů
      this.handleError();
    }
  }

  convertFileToBase64(file: File): Promise<string> {
    return new Promise<string> ((resolve,reject)=> {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        let encoded = reader.result?.toString().replace(/^data:(.*,)?/, '');
        if (encoded && (encoded.length % 4) > 0) {
          encoded += '='.repeat(4 - (encoded.length % 4));
        }
        resolve(encoded || "");
      };

      reader.onerror = error => reject(error);
  })
  }

  downloadBase64Document(documentDTO: DocumentDTO): void {
    try {
      const a = document.createElement('a');

      a.style.display = 'none';
      a.href = "data:application/octet-stream;base64," + documentDTO.base64Content;
      a.download = documentDTO.fileNameWithExtension || "";
      
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (error) {
      // TODO: globální zpracování errorů
      console.log(document);
      this.handleError();
    }
  }

  private handleError() {
    this._dialogService.openDialog("Chyba", () =>
      React.createElement(
        "div",
        { className: "alert alert-danger m-4" },
        "Požadovaný soubor se nepodařilo stáhnout."
      )
    );
  }

  private getFileName(response: AxiosResponse, fileName?: string): string {
    let dispositionFileName: Nullable<string> = null;

    if (fileName) {
      return fileName;
    }
    else if (response.headers["content-disposition"]) {
      let disposition = String(response.headers["content-disposition"]);

      if (disposition && disposition.indexOf('attachment') !== -1) {
        dispositionFileName = this.getUtf8FileName(disposition);
        dispositionFileName = dispositionFileName !== null ? dispositionFileName : this.getSanitizedFileName(disposition);
      }
    } 

    return dispositionFileName ?? "UnknownFile";
  }

  private getUtf8FileName(disposition: string): Nullable<string> {
    let filenameRegex = /filename\*?=([^']*'')?([^;]*)/;
    let matches = filenameRegex.exec(disposition);

    if (matches !== null && matches[2]) { 
      return decodeURIComponent(matches[2]);
    }

    return null;
  }

  private getSanitizedFileName(disposition: string): Nullable<string> {
    let filenameRegex = (/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);

    let matches = filenameRegex.exec(disposition);
    if (matches !== null && matches[1]) { 
      return matches[1].replace(/['"]/g, '');
    }

    return null;
  }
}